/**
 * live-events — global custom-event bus for partner cabinet "live" effects.
 *
 * partner-sse.ts dispatches `partner:conversion` / `partner:payout` events
 * on window when SSE fires. Components subscribe via the hooks below.
 *
 * In dev (mocks only), the StateSwitcher's "+conv" button calls
 * dispatchSyntheticConversion() to test confetti / tab-heartbeat / sound
 * without a real backend.
 */

import { useEffect, useState } from 'react';

export interface LiveConversionDetail {
  id: string;
  amountCents: number;
  offerLabel?: string;
  countryCode?: string;
  iso: string; // ISO timestamp
}

export interface LivePayoutDetail {
  id: string;
  amountCents: number;
  iso: string;
}

declare global {
  interface WindowEventMap {
    'partner:conversion': CustomEvent<LiveConversionDetail>;
    'partner:payout': CustomEvent<LivePayoutDetail>;
    'partner:sse-state': CustomEvent<{ online: boolean }>;
  }
}

export function emitConversion(detail: LiveConversionDetail): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent('partner:conversion', { detail }));
}

export function emitPayout(detail: LivePayoutDetail): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent('partner:payout', { detail }));
}

export function emitSseState(online: boolean): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent('partner:sse-state', { detail: { online } }));
}

/** Hook: subscribe to live conversion events. Returns the most-recent detail (with a tick counter to retrigger effects on identical payloads). */
export function useLiveConversion(): { last: LiveConversionDetail | null; tick: number } {
  const [state, setState] = useState<{ last: LiveConversionDetail | null; tick: number }>({
    last: null,
    tick: 0,
  });
  useEffect(() => {
    const onConv = (e: WindowEventMap['partner:conversion']) => {
      setState((s) => ({ last: e.detail, tick: s.tick + 1 }));
    };
    window.addEventListener('partner:conversion', onConv);
    return () => window.removeEventListener('partner:conversion', onConv);
  }, []);
  return state;
}

/** Hook: tracks SSE online/offline state via partner-sse heartbeat + native events. */
export function useSseOnline(): boolean {
  const [online, setOnline] = useState(true);
  useEffect(() => {
    const onState = (e: WindowEventMap['partner:sse-state']) => setOnline(e.detail.online);
    window.addEventListener('partner:sse-state', onState);
    const onOffline = () => setOnline(false);
    const onOnline = () => setOnline(true);
    window.addEventListener('offline', onOffline);
    window.addEventListener('online', onOnline);
    return () => {
      window.removeEventListener('partner:sse-state', onState);
      window.removeEventListener('offline', onOffline);
      window.removeEventListener('online', onOnline);
    };
  }, []);
  return online;
}

/** Dev affordance — simulate a conversion event so motion/sound/notification effects can be tested without a live backend. */
export function dispatchSyntheticConversion(amountCents = 5800, offer = '/crypto-casino · RU'): void {
  emitConversion({
    id: `dev-${Date.now()}`,
    amountCents,
    offerLabel: offer,
    iso: new Date().toISOString(),
  });
}
